import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Calendar, CreditCard, LogOut, MapPin, ShieldCheck, Heart } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MembershipCheckout } from "../components/MembershipCheckout";
import { api, memberHeaders, clearMemberToken, getMemberToken } from "../lib/api";
import { LINKS } from "../lib/constants";
import { monthShort, dayNum, timeRange, fullDate } from "../lib/date";

const STATUS_STYLE = {
  active: "bg-trailride text-white",
  active_donor: "bg-trailride text-white",
  expired: "bg-amber-100 text-amber-800 border border-amber-300",
  unpaid: "bg-ink/5 text-ink/70 border border-ink/15",
};

export default function MemberDashboard() {
  const [data, setData] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const navigate = useNavigate();

  const loadMe = () => api.get("/member/me", { headers: memberHeaders() }).then((r) => setData(r.data));

  useEffect(() => {
    if (!getMemberToken()) {
      navigate("/member-login");
      return;
    }
    loadMe()
      .catch(() => {
        clearMemberToken();
        navigate("/member-login");
      })
      .finally(() => setLoading(false));
    api.get("/membership-info").then((r) => setInfo(r.data)).catch(() => {});
  }, [navigate]);

  const logout = () => {
    clearMemberToken();
    navigate("/member-login");
  };

  if (loading) {
    return (
      <div className="bg-paper min-h-screen flex items-center justify-center">
        <p className="font-sans text-ink/40 animate-pulse">Loading your dashboard…</p>
      </div>
    );
  }

  const member = data?.member;
  const events = data?.upcoming_events || [];
  const status = member?.account_status || { code: "unpaid", label: "Account created · dues unpaid" };
  const isActive = status.code === "active" || status.code === "active_donor";
  const dues = info?.dues_amount ?? 0;

  return (
    <div className="bg-paper min-h-screen" data-testid="member-dashboard-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20">
        <div className="flex items-start justify-between gap-4 mb-14">
          <div>
            <p className="font-sans text-xs tracking-[0.24em] uppercase text-trailride font-semibold mb-4">
              {member?.role === "officer" ? "Officer" : "Member"} Dashboard
            </p>
            <h1 className="font-serif text-4xl md:text-6xl font-black tracking-tight text-ink leading-none">
              Welcome, {member?.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="font-sans text-ink/60 mt-4">Glad you're moving with us. Here's what's ahead.</p>
          </div>
          <button
            onClick={logout}
            data-testid="member-logout"
            className="inline-flex items-center gap-2 font-sans text-sm text-ink/60 hover:text-ink border border-ink/15 rounded-full px-4 py-2 transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Required events */}
          <div className="md:col-span-2 border border-ink/10 p-6 md:p-8" data-testid="dashboard-events">
            <div className="flex items-center gap-2 mb-6">
              <Calendar size={18} className="text-trailride" />
              <h2 className="font-serif text-2xl font-bold text-ink">Upcoming required events</h2>
            </div>
            {events.length === 0 ? (
              <p className="font-sans text-ink/50">No upcoming events posted yet. Check back soon.</p>
            ) : (
              <ul className="divide-y divide-ink/10">
                {events.map((e) => (
                  <li key={e.id} className="py-4 flex items-center gap-5" data-testid={`dashboard-event-${e.id}`}>
                    <div className="flex flex-col items-center text-trailride shrink-0 w-14">
                      <span className="font-sans font-semibold text-xs tracking-widest uppercase">{monthShort(e.start)}</span>
                      <span className="font-serif font-bold text-2xl leading-none">{dayNum(e.start)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans font-semibold text-ink truncate">{e.title}</p>
                      <p className="font-sans text-sm text-ink/55 flex items-center gap-3 flex-wrap">
                        <span>{timeRange(e.start, e.end)}</span>
                        <span className="inline-flex items-center gap-1"><MapPin size={12} /> {e.location}</span>
                      </p>
                    </div>
                    {e.tigerlink_url && (
                      <a href={e.tigerlink_url} target="_blank" rel="noreferrer" className="text-trailride shrink-0" aria-label="RSVP on TigerLink">
                        <ArrowUpRight size={18} />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-6 md:space-y-8">
            {/* Membership & dues */}
            <div className="border border-ink/10 p-6 md:p-8" data-testid="dashboard-dues">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard size={18} className="text-trailride" />
                <h2 className="font-serif text-xl font-bold text-ink">Membership</h2>
              </div>
              <span
                className={`inline-flex items-center font-sans text-sm font-semibold px-4 py-1.5 rounded-full ${STATUS_STYLE[status.code] || STATUS_STYLE.unpaid}`}
                data-testid="dues-badge"
              >
                {status.label}
              </span>

              {isActive && member?.dues_expires_at && (
                <p className="font-sans text-sm text-ink/55 mt-3">Active through {fullDate(member.dues_expires_at)}.</p>
              )}
              {isActive && (member?.donation_amount || 0) > 0 && (
                <p className="font-sans text-sm text-trailride mt-2 inline-flex items-center gap-1.5">
                  <Heart size={14} /> Thank you for your ${Number(member.donation_amount).toFixed(2)} donation.
                </p>
              )}

              {!isActive && (
                <>
                  <p className="font-sans text-sm text-ink/55 mt-3">
                    {status.code === "expired"
                      ? "Your membership period has ended. Renew to regain active access."
                      : "Pay your annual dues to unlock full membership access."}
                  </p>
                  <button
                    onClick={() => setCheckoutOpen(true)}
                    data-testid="pay-dues-button"
                    className="w-full inline-flex items-center justify-center gap-2 mt-5 px-6 py-3.5 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark transition-colors"
                  >
                    <ShieldCheck size={18} /> {status.code === "expired" ? "Renew" : "Pay annual dues"} · ${dues.toFixed(2)}
                  </button>
                </>
              )}
              {isActive && (
                <button
                  onClick={() => setCheckoutOpen(true)}
                  data-testid="renew-dues-button"
                  className="font-sans text-sm text-trailride font-medium mt-4 hover:underline underline-offset-4"
                >
                  Renew early or add a donation
                </button>
              )}
            </div>

            {/* Links */}
            <div className="border border-ink/10 p-6 md:p-8" data-testid="dashboard-links">
              <h2 className="font-serif text-xl font-bold text-ink mb-4">Important links</h2>
              <ul className="space-y-3 font-sans text-sm">
                <li><a href={LINKS.tigerlink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-trailride hover:gap-2.5 transition-all">TigerLink page <ArrowUpRight size={14} /></a></li>
                <li><a href={LINKS.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-trailride hover:gap-2.5 transition-all">Instagram <ArrowUpRight size={14} /></a></li>
                <li><Link to="/events" className="inline-flex items-center gap-1.5 text-trailride hover:gap-2.5 transition-all">All events</Link></li>
                <li><a href={`mailto:${LINKS.email}`} className="text-trailride">{LINKS.email}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <MembershipCheckout open={checkoutOpen} onClose={() => setCheckoutOpen(false)} isRenewal={isActive} />
    </div>
  );
}
